export const STRIP_TILE_SIZE = 1080;
export const STRIP_BORDER = 60;
export const STRIP_FOOTER = 80;

export interface StripLayout {
  width: number;
  height: number;
  tileSize: number;
  border: number;
  footer: number;
  tiles: { x: number; y: number; size: number }[];
}

export function computeStripLayout(shots: number): StripLayout {
  const n = Math.max(1, shots);
  const tileSize = STRIP_TILE_SIZE;
  const border = STRIP_BORDER;
  const footer = n === 1 ? 0 : STRIP_FOOTER;
  const width = tileSize + border * 2;
  const height = tileSize * n + border * (n + 1) + footer;
  const tiles: StripLayout['tiles'] = [];
  for (let i = 0; i < n; i++) {
    tiles.push({
      x: border,
      y: border + i * (tileSize + border),
      size: tileSize,
    });
  }
  return { width, height, tileSize, border, footer, tiles };
}

async function blobToBitmap(blob: Blob): Promise<ImageBitmap> {
  return await createImageBitmap(blob);
}

function drawCover(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  src: ImageBitmap,
  x: number,
  y: number,
  size: number,
): void {
  const sw = src.width;
  const sh = src.height;
  const sAspect = sw / sh;
  let sx = 0;
  let sy = 0;
  let scw = sw;
  let sch = sh;
  if (sAspect > 1) {
    // Wider than tall → trim sides.
    scw = sh;
    sx = (sw - scw) / 2;
  } else if (sAspect < 1) {
    sch = sw;
    sy = (sh - sch) / 2;
  }
  ctx.drawImage(src, sx, sy, scw, sch, x, y, size, size);
}

function formatStamp(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `PHOTOBOOTH · ${y}-${m}-${d}`;
}

export interface ComposeStripOptions {
  frames: Blob[];
  jpegQuality?: number;
  stamp?: string;
  /**
   * Optional template art for this strip (e.g. uploaded via the builder).
   * If present:
   *   - the URL is resized to the strip dimensions and drawn ON TOP of the
   *     photos, so transparent regions reveal the photos beneath;
   *   - the default white background + date footer are suppressed (the
   *     template is expected to provide its own surrounding art).
   */
  printFrameUrl?: string | null;
}

export async function composeStrip(opts: ComposeStripOptions): Promise<Blob> {
  const { frames, printFrameUrl } = opts;
  if (frames.length === 0) throw new Error('composeStrip requires at least 1 frame');
  const layout = computeStripLayout(frames.length);
  const bitmaps = await Promise.all(frames.map(blobToBitmap));

  let canvas: HTMLCanvasElement | OffscreenCanvas;
  let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  if (typeof OffscreenCanvas !== 'undefined') {
    canvas = new OffscreenCanvas(layout.width, layout.height);
    ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
  } else {
    canvas = document.createElement('canvas');
    canvas.width = layout.width;
    canvas.height = layout.height;
    ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  }
  if (!ctx) throw new Error('2D context unavailable');

  // Background — only used when there's no template art.
  if (!printFrameUrl) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, layout.width, layout.height);
  } else {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, layout.width, layout.height);
  }

  for (let i = 0; i < bitmaps.length; i++) {
    const tile = layout.tiles[i]!;
    drawCover(ctx, bitmaps[i]!, tile.x, tile.y, tile.size);
  }

  if (printFrameUrl) {
    try {
      const tplBlob = await fetch(printFrameUrl).then((r) => r.blob());
      const tplBitmap = await createImageBitmap(tplBlob);
      // Stretch to strip dims; user already cropped to PRINT_FRAME_TARGET.
      ctx.drawImage(tplBitmap, 0, 0, layout.width, layout.height);
      tplBitmap.close();
    } catch (err) {
      console.warn('[strip] print-frame overlay failed; continuing without', err);
    }
  } else if (layout.footer > 0) {
    ctx.fillStyle = '#111';
    const text = opts.stamp ?? formatStamp();
    ctx.font = 'bold 36px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      text,
      layout.width / 2,
      layout.height - layout.footer / 2,
    );
  }

  // Release bitmaps as soon as we're done painting.
  for (const b of bitmaps) b.close();

  const quality = opts.jpegQuality ?? 0.92;
  if (canvas instanceof OffscreenCanvas) {
    return await canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      quality,
    );
  });
}
